import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { computed, inject } from '@angular/core';
import { Router } from '@angular/router';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { catchError, EMPTY, pipe, switchMap, tap } from 'rxjs';
import { ConverseApi } from '../../service-gateways/converse/converse.api';
import { getOrganizationFromRouter } from '../../core/utils/organization-router.util';
import {
  ConversationMessage,
  ConverseEvent,
  ToolConfirmation,
} from '../../service-gateways/converse/converse.interfaces';
import { buildDisplayMessages } from './co-visr-display.utils';
import { reduceConverseEvent } from './co-visr-converse.utils';
import { CoVisrWarning, PendingToolCall } from './co-visr.interfaces';

interface CoVisrState {
  messages: ConversationMessage[];
  isOpen: boolean;
  isStreaming: boolean;
  streamingText: string;
  streamingMessageId: string | null;
  /** Tracks tool calls received during the current stream to determine which need confirmation. */
  pendingToolCalls: PendingToolCall[];
  /** Tool calls requiring confirmation, held until the user's next message. */
  pendingConfirmations: ToolConfirmation[];
  /** Assistant message held back from history while awaiting confirmation. */
  heldAssistantMessage: ConversationMessage | null;
  /** Mutation tools that were confirmed but failed — sent as retryConfirmedForTools so the server skips re-confirmation. */
  failedMutationTools: string[];
  /** Warnings the chat renders itself above the reply they belong to (see CoVisrWarning). */
  warnings: CoVisrWarning[];
  error: string | null;
}

const initialState: CoVisrState = {
  messages: [],
  isOpen: false,
  isStreaming: false,
  streamingText: '',
  streamingMessageId: null,
  pendingToolCalls: [],
  pendingConfirmations: [],
  heldAssistantMessage: null,
  failedMutationTools: [],
  warnings: [],
  error: null,
};

let messageCounter = 0;
const nextMessageId = () => `msg-${++messageCounter}`;

export const CoVisrStore = signalStore(
  withState(initialState),
  withComputed((store) => ({
    displayMessages: computed(() =>
      buildDisplayMessages({
        messages: store.messages(),
        heldAssistantMessage: store.heldAssistantMessage(),
        streamingText: store.streamingText(),
        isStreaming: store.isStreaming(),
        streamingMessageId: store.streamingMessageId(),
        warnings: store.warnings(),
      }),
    ),
    isWaitingForResponse: computed(() => store.isStreaming() && store.streamingText() === ''),
    isReceivingText: computed(() => store.isStreaming() && store.streamingText() !== ''),
    hasPendingConfirmations: computed(() => store.pendingConfirmations().length > 0),
    pendingConfirmationToolNames: computed(() => store.pendingConfirmations().map((tc) => tc.toolName)),
  })),
  withMethods((store, converseApi = inject(ConverseApi), router = inject(Router)) => ({
    openDrawer: () => patchState(store, { isOpen: true }),
    closeDrawer: () => patchState(store, { isOpen: false }),
    clearChat: () => {
      messageCounter = 0;
      patchState(store, {
        messages: [],
        streamingText: '',
        error: null,
        isStreaming: false,
        pendingToolCalls: [],
        pendingConfirmations: [],
        heldAssistantMessage: null,
        failedMutationTools: [],
        warnings: [],
      });
    },
    sendMessage: rxMethod<string>(
      pipe(
        tap((text) => {
          const updatedMessages = [...store.messages()];

          // If there's a held assistant message awaiting confirmation, add it to history now
          const held = store.heldAssistantMessage();
          if (held) {
            updatedMessages.push(held);
          }

          const userMessage: ConversationMessage = {
            id: nextMessageId(),
            role: 'user',
            parts: [{ type: 'text', text }],
          };
          updatedMessages.push(userMessage);

          patchState(store, {
            messages: updatedMessages,
            heldAssistantMessage: null,
            isStreaming: true,
            streamingText: '',
            streamingMessageId: nextMessageId(),
            pendingToolCalls: [],
            error: null,
          });
        }),
        switchMap(() => {
          const confirmations = store.pendingConfirmations().length > 0 ? store.pendingConfirmations() : undefined;
          patchState(store, { pendingConfirmations: [] });

          const orgContext = getOrganizationFromRouter(router) ?? undefined;
          const retryTools = store.failedMutationTools().length > 0 ? store.failedMutationTools() : undefined;
          return converseApi.converse(store.messages(), confirmations, orgContext, retryTools).pipe(
            tap((event: ConverseEvent) => {
              const snapshot = {
                streamingText: store.streamingText(),
                pendingToolCalls: store.pendingToolCalls(),
                failedMutationTools: store.failedMutationTools(),
                messages: store.messages(),
                streamingMessageId: store.streamingMessageId(),
                warnings: store.warnings(),
              };
              const patch = reduceConverseEvent(event, snapshot);
              patchState(store, patch);
            }),
            catchError((err) => {
              patchState(store, {
                error: err?.message ?? 'An unexpected error occurred',
                isStreaming: false,
                streamingText: '',
                streamingMessageId: null,
                pendingToolCalls: [],
              });
              return EMPTY;
            }),
          );
        }),
      ),
    ),
    confirmTools: rxMethod<boolean>(
      pipe(
        tap((accepted) => {
          const updatedMessages = [...store.messages()];
          const held = store.heldAssistantMessage();
          if (held) {
            updatedMessages.push(held);
          }
          const userMessage: ConversationMessage = {
            id: nextMessageId(),
            role: 'user',
            parts: [{ type: 'text', text: accepted ? 'Yes' : 'No' }],
          };
          updatedMessages.push(userMessage);
          patchState(store, {
            messages: updatedMessages,
            heldAssistantMessage: null,
            isStreaming: true,
            streamingText: '',
            streamingMessageId: nextMessageId(),
            pendingToolCalls: [],
            error: null,
          });
        }),
        switchMap((accepted) => {
          const pending = store.pendingConfirmations();
          patchState(store, { pendingConfirmations: [] });
          const confirmations: ToolConfirmation[] = pending.map((tc) => ({
            ...tc,
            confirmed: accepted,
          }));
          const orgContext = getOrganizationFromRouter(router) ?? undefined;
          const retryTools = store.failedMutationTools().length > 0 ? store.failedMutationTools() : undefined;
          return converseApi.converse(store.messages(), confirmations, orgContext, retryTools).pipe(
            tap((event: ConverseEvent) => {
              const snapshot = {
                streamingText: store.streamingText(),
                pendingToolCalls: store.pendingToolCalls(),
                failedMutationTools: store.failedMutationTools(),
                messages: store.messages(),
                streamingMessageId: store.streamingMessageId(),
                warnings: store.warnings(),
              };
              const patch = reduceConverseEvent(event, snapshot);
              patchState(store, patch);
            }),
            catchError((err) => {
              patchState(store, {
                error: err?.message ?? 'An unexpected error occurred',
                isStreaming: false,
                streamingText: '',
                streamingMessageId: null,
                pendingToolCalls: [],
              });
              return EMPTY;
            }),
          );
        }),
      ),
    ),
  })),
);
